import { createQueryKeys } from "@lukemorales/query-key-factory";
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { api } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import {
  type ReviewPeriod,
  type ReviewPeriodSummaryResponse,
  type ReviewPeriodTypesSummaryResponse,
  type ReviewTypesMeSummaryResponse,
  type ReviewUsersSummary,
} from "../types";

export type ReviewPeriodList = Paginate<ReviewPeriod<true>>;

export const reviewPeriods = createQueryKeys("performance-review-periods", {
  all: (filter?: SearchRequest) => [{ filter }],
  one: (id?: number) => [id],
  summary: (id?: number) => ["summary", id],
  typesSummary: (id?: number) => ["types-summary", id],
  meSummary: (id?: number) => ["me-summary", id],
  usersSummary: (id: number, page?: number) => ["users-summary", { id, page }],
});

export type usersSummaryList = Paginate<ReviewUsersSummary<true, true, true>>;

const include = "reviewPeriodTypes";

export const fetchReviewPeriodsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...reviewPeriods.all(search),
    queryFn: () =>
      api
        .get<ReviewPeriodList>(`/performance/review-periods`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchReviewPeriods = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchReviewPeriodsQueryOptions(page, search));

export const fetchReviewPeriodOptions = (id?: number) =>
  queryOptions({
    ...reviewPeriods.one(id),
    queryFn: () =>
      api
        .get<ReviewPeriod<true>>(`/performance/review-periods/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriod = (id?: number) =>
  useQuery(fetchReviewPeriodOptions(id));

export const fetchReviewPeriodSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...reviewPeriods.summary(id),
    queryFn: () =>
      api
        .get<ReviewPeriodSummaryResponse>(
          `/performance/review-periods/${id}/summary`,
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriodSummary = (id?: number) =>
  useQuery(fetchReviewPeriodSummaryQueryOptions(id));

export const fetchReviewPeriodTypesSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...reviewPeriods.typesSummary(id),
    queryFn: () =>
      api
        .get<ReviewPeriodTypesSummaryResponse>(
          `/performance/review-periods/${id}/types/summary`,
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriodTypesSummary = (id?: number) =>
  useQuery(fetchReviewPeriodTypesSummaryQueryOptions(id));

export const fetchReviewPeriodUsersSummaryQueryOptions = (
  id: number,
  page: number = 0,
) =>
  queryOptions({
    ...reviewPeriods.usersSummary(id, page),
    queryFn: () =>
      api
        .get<usersSummaryList>(
          `/performance/review-periods/${id}/users/summary`,
          {
            params: { page, include: "user.position.unit" },
          },
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriodUsersSummary = (id: number) =>
  usePaginateQuery((page) =>
    fetchReviewPeriodUsersSummaryQueryOptions(id, page),
  );

export const fetchReviewPeriodMeSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...reviewPeriods.meSummary(id),
    queryFn: () =>
      api
        .get<ReviewTypesMeSummaryResponse>(
          `/performance/review-periods/${id}/me/summary`,
        )
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchReviewPeriodMeSummary = (id?: number) =>
  useQuery(fetchReviewPeriodMeSummaryQueryOptions(id));

export const useDownloadReviewPeriodReport = () =>
  useMutation({
    mutationFn: (id: number) =>
      api.get<Blob>(`/performance/review-periods/${id}/export`, {
        responseType: "blob",
      }),
  });
